import { defineStore } from 'pinia'
import utils from '@/utils'
import { showToast } from 'vant'
import {
  callBluetooth,
  clearBluetoothBridge,
  NVEASY_PLAY_STATE,
  NVEASY_BLUETOOTH_CONFIG,
  NVEASY_BLUETOOTH_STATE,
  registerBluetoothBridge
} from '@/utils/nveasy-bluetooth-bridge'
import { getSignature } from '@/api/app.js'
import {
  checkBluetoothBadgeConnect,
  bindBluetoothBadge,
  unbindBluetoothBadge,
  getBluetoothBadgeBindingDetail,
  reportBluetoothBadgeStatus,
  // 文件模块
  getBluetoothBadgeRecordList,
  saveBluetoothBadgeRecord
} from '@/api/nveasy-bt.js'
import xbb from '@xbb/xbb-utils'

let activeUploadBatch = null

const logoutBluetoothCommand = async (action) => {
  let timer
  try {
    await Promise.race([
      callBluetooth(action),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${action} 超时`)), 1500)
      })
    ])
  } catch (error) {
    console.warn(`退出时蓝牙 ${action} 失败`, error)
  } finally {
    clearTimeout(timer)
  }
}

// 格式化文件信息
const getFormatInfo = (nameStr) => {
  const [namePrefix, startTime, durationSeconds] = nameStr.replace('.opus', '').split('_')
  return {
    name: namePrefix,
    startTime: xbb.formatTime(Number(startTime) * 1000, 'yyyy-MM-dd HH:mm:ss'),
    endTime: xbb.formatTime(
      (Number(startTime) + Number(durationSeconds)) * 1000,
      'yyyy-MM-dd HH:mm:ss'
    )
  }
}
export const useNveasyBluetoothStore = defineStore('nveasyBluetooth', {
  state: () => ({
    devices: [],
    files: [], // 设备文件列表
    ossFiles: [], // 阿里云存储列表
    scanning: false,
    scanPolling: false,
    connectingDeviceId: '',
    connectionAttempt: null,
    playStatus: 0, // 播放状态 `status`：0 停止，1 开始，2 暂停，3 继续（暂停/继续需设备支持 `ABILITY_RECORD_PAUSE_RESUME`）
    downloadInfo: {
      currentCount: 0, // 当前传输的位置
      totalCount: 0, // 当前传输的文件
      currentPacket: 0, // 当前传输文件的分包index
      totalPackets: 0 // 当前传输文件的总分包count
    },
    uploadInfo: {
      fileList: [], // 当前传输的文件list
      successList: [], // 当前传输成功的文件list
      errorList: [] // 当前传世失败的文件list
    },
    bindDeviceInfo: {
      name: '',
      deviceId: '',
      hardwareVersion: '',
      idMark: '',
      queryBattery: {},
      queryStorage: {}
    }, // 当前绑定的设备配置
    bindingDetail: null, // 绑定详情快照，仅用于同设备状态上报补位
    currentState: '',
    initialized: false
  }),

  getters: {
    // 是否绑定
    isBind: (state) => Boolean(state.bindDeviceInfo?.deviceId),
    // 是否连接
    isConnected: (state) => state.currentState === NVEASY_BLUETOOTH_STATE.BOUND_CONNECTED,
    isLowBattery: (state) => {
      if (!state.bindDeviceInfo?.deviceId) return false
      const battery = state.bindDeviceInfo?.queryBattery?.caseBattery
      return (
        battery !== null &&
        battery !== undefined &&
        battery !== '' &&
        Number.isFinite(Number(battery)) &&
        Number(battery) <= 20
      )
    },
    // 当前传输进度
    downloadProgress: (state) => {
      const {
        currentCount, // 当前传输的位置
        totalCount, // 当前传输的文件
        currentPacket, // 当前传输文件的分包index
        totalPackets // 当前传输文件的总分包count
      } = state.downloadInfo
      return {
        currentCount,
        totalCount,
        currentPacket,
        totalPackets,
        isPending: Boolean(totalCount) // 正在传输中
      }
    },
    // 当前上传进度
    uploadProgress: (state) => {
      const {
        fileList = [], // 当前传输的文件list
        errorList = [], // 当前传世失败的文件list
        successList = [] // 当前传输成功的文件list
      } = state.uploadInfo

      const formatFileList = fileList.map((item) => {
        const isSuccessItem = successList.find((successItem) => successItem.name === item.name)
        const isErrorItem = errorList.find((errorItem) => errorItem.name === item.name)
        if (isSuccessItem) {
          return {
            ...item,
            ...isSuccessItem,
            ...getFormatInfo(item.name),
            success: true
          }
        } else if (isErrorItem) {
          return {
            ...item,
            ...isErrorItem,
            ...getFormatInfo(item.name),
            success: false
          }
        } else {
          return {
            ...item,
            ...getFormatInfo(item.name)
          }
        }
      })
      return {
        totalCount: fileList.length,
        errorCount: errorList.length,
        successCount: successList.length,
        formatFileList,
        isPending: Boolean(fileList.length) // 正在传输中
      }
    }
  },

  actions: {
    /**
     * 覆盖当前扫描到的蓝牙设备列表。
     */
    setDevices(devices) {
      this.devices = devices
    },
    /**
     * 更新蓝牙扫描状态，用于页面展示扫描中/已停止。
     */
    setScanning(scanning) {
      this.scanning = scanning
    },
    /**
     * 更新蓝牙扫描轮询状态，用于控制 scan-finished 后是否继续扫描。
     * 注意：需要当前页面关闭及时释放
     */
    setScanPolling(scanPolling) {
      this.scanPolling = scanPolling
    },
    /**
     * 记录正在连接的设备，避免重复发起连接。
     */
    setConnectingDeviceId(connectingDeviceId) {
      this.connectingDeviceId = connectingDeviceId
    },
    /**
     * 更新当前已绑定或已连接设备信息。
     */
    setBindDeviceInfo(bindDeviceInfo) {
      const nextDevice = bindDeviceInfo || {}
      if (this.bindingDetail?.deviceNo !== nextDevice.deviceId) this.bindingDetail = null
      this.bindDeviceInfo = nextDevice
    },
    /**
     * 更新当前设备文件信息。
     */
    setFiles(files) {
      this.files = files || []
    },
    /**
     * 更新oss文件信息。
     */
    setOssFiles(files) {
      this.ossFiles = files || []
    },
    /**
     * 更新蓝牙业务状态，驱动页面不同空态与连接态。
     */
    setCurrentState(currentState) {
      this.currentState = currentState
    },
    /**
     * 标记 App 级蓝牙初始化是否已执行。
     */
    setInitialized(initialized) {
      this.initialized = initialized
    },
    /**
     * 设置播放状态
     */
    setPlayStatus(playStatus) {
      this.playStatus = playStatus
    },
    async resetBluetoothSession() {
      this.setScanPolling(false)
      try {
        if (this.playStatus !== NVEASY_PLAY_STATE.STOP) {
          await logoutBluetoothCommand('stopRecord')
        }
        await logoutBluetoothCommand('stopScan')
        await logoutBluetoothCommand('disconnect')
      } finally {
        this.clearBridge()
        activeUploadBatch = null
        this.$reset()
      }
    },
    /**
     * 设置下载状态
     */
    setDownloadInfo(downloadInfo = {}, init = false) {
      if (init) {
        this.downloadInfo = {
          currentCount: 0, // 当前传输的位置
          totalCount: 0, // 当前传输的文件
          currentPacket: 0, // 当前传输文件的分包index
          totalPackets: 0 // 当前传输文件的总分包count
        }
        return
      }
      this.downloadInfo = {
        ...this.downloadInfo,
        ...downloadInfo
      }
    },
    /**
     * 设置上传状态
     */
    setUploadInfo(uploadInfo = {}, init = false) {
      if (init) {
        this.uploadInfo = {
          fileList: [], // 当前传输的文件list
          errorList: [], // 当前传输失败的文件list
          successList: [] // 当前传输成功的文件list
        }
        return
      }
      this.uploadInfo = {
        ...this.uploadInfo,
        ...uploadInfo
      }
    },
    /**
     * 新增或合并扫描到的设备，按 deviceId 保持列表唯一。
     */
    upsertDevice(device) {
      const deviceId = device.deviceId
      if (!deviceId) return

      const currentDevice = {
        ...device
      }
      const index = this.devices.findIndex((item) => item.deviceId === deviceId)

      if (index >= 0) {
        this.devices.splice(index, 1, {
          ...this.devices[index],
          ...currentDevice
        })
        return
      }

      this.devices.push(currentDevice)
    },
    handleUploadEvent(message) {
      const { eventName, data = {} } = message
      const { batchId } = data
      if (eventName === 'upload-start') {
        const { fileList = [] } = data
        if (batchId) {
          activeUploadBatch = {
            id: batchId,
            saveTasks: [],
            uploadedNames: new Set(),
            total: fileList.length,
            processed: 0
          }
          console.log(
            '[NVEasy upload] batch started:',
            batchId,
            fileList.map((file) => file.name)
          )
        }
        this.setUploadInfo({ successList: [], errorList: [], fileList })
        return
      }
      if (eventName === 'upload-reconcile') {
        if (!batchId) return
        const { fileNames = [], total, processed } = data
        const batch =
          activeUploadBatch?.id === batchId
            ? activeUploadBatch
            : { id: batchId, saveTasks: [], uploadedNames: new Set(), total, processed }
        batch.total = total ?? batch.total
        batch.processed = processed ?? batch.processed
        for (const name of fileNames) batch.uploadedNames.add(name)
        this.confirmUploadBatch(batch, true)
        return
      }
      if (eventName !== 'upload-progress') {
        if (
          !batchId &&
          !activeUploadBatch &&
          (eventName === 'upload-finished' || eventName === 'upload-error')
        ) {
          this.setUploadInfo({}, true)
        }
        return
      }
      const {
        file: item,
        success,
        isLast,
        successFiles: successList = [],
        errorFiles: errorList = []
      } = data
      const batch = activeUploadBatch?.id === batchId ? activeUploadBatch : null
      if (batchId && !batch) return
      if (batch) batch.processed = Math.max(batch.processed, data.finished || 0)
      if (!success) {
        this.setUploadInfo({ errorList })
      } else {
        const params = {
          key: item.name,
          deviceNo: this.bindDeviceInfo.deviceId,
          fileUrl: item.url,
          fileName: item.name,
          fileSize: item.size,
          ...getFormatInfo(item.name)
        }
        if (batch) batch.uploadedNames.add(item.name)
        const saveTask = saveBluetoothBadgeRecord(params)
          .then(() => {
            if (!batch || activeUploadBatch === batch) this.setUploadInfo({ successList })
          })
          .catch(() => {
            if (!batch || activeUploadBatch === batch) {
              this.setUploadInfo({ errorList: [...errorList, item] })
            }
          })
        if (batch) batch.saveTasks.push(saveTask)
        else if (isLast) saveTask.finally(() => this.getOssFiles())
      }
      if (batch && isLast) this.confirmUploadBatch(batch)
      else if (!batch && isLast && !success) this.getOssFiles()
    },
    async confirmUploadBatch(batch, reconcile = false) {
      if (!batch || (activeUploadBatch !== batch && (!reconcile || activeUploadBatch))) return
      if (batch.confirming) return batch.confirming
      batch.confirming = (async () => {
        await Promise.allSettled(batch.saveTasks)
        if (activeUploadBatch !== batch && (!reconcile || activeUploadBatch)) return
        try {
          const files = await this.getOssFiles()
          if (activeUploadBatch !== batch && (!reconcile || activeUploadBatch)) return
          const savedNames = new Set(
            files.map((file) => file.fileName?.replace(/\.wav$/i, '.opus'))
          )
          const verified =
            batch.total > 0 &&
            batch.processed === batch.total &&
            [...batch.uploadedNames].every((name) => savedNames.has(name))
          console.log('[NVEasy upload] batch check', batch.id, {
            uploaded: [...batch.uploadedNames],
            verified
          })
          if (!verified) return
          if (typeof window !== 'undefined' && window.uni?.postMessage) {
            window.uni.postMessage({
              data: { type: 'nveasyBluetoothUploadConfirmed', batchId: batch.id, verified: true }
            })
          }
          if (activeUploadBatch === batch) {
            activeUploadBatch = null
            this.setUploadInfo({}, true)
          }
        } catch (error) {
          console.warn('[NVEasy upload] list check failed:', batch.id, error)
        }
      })()
      try {
        await batch.confirming
      } finally {
        batch.confirming = null
      }
    },
    /**
     * 处理原生蓝牙桥回调；扫描到已绑定设备时自动重连。
     */
    async handleBluetoothEvent(message) {
      if (message.eventName === 'scan') {
        // 如果是扫描设备监听（每扫描到一个设备触发一次）
        const scanDevice = message.data || {}
        // 如果--当前已经绑定设备
        if (this.bindDeviceInfo.deviceId) {
          // 绑定的设备和扫描的一致，直接连接
          const isSame = this.bindDeviceInfo.deviceId === scanDevice.deviceId
          if (isSame && !this.isConnected) this.reconnectBoundDevice(scanDevice).catch(() => {})
          return
        } else {
          // 如果--当前没有绑定设备，渲染设备列表
          this.upsertDevice(scanDevice)
        }
      } else if (message.eventName === 'scan-finished') {
        // 扫描设备，结束event
        this.setScanning(false)
        if (this.scanPolling) {
          setTimeout(() => {
            this.startScan().catch(() => {
              this.stopScanPolling()
            })
          }, 5000)
        }
      } else if (message.eventName === 'battery') {
        // 获取电池info
        this.setBindDeviceInfo({
          ...this.bindDeviceInfo,
          queryBattery: message.data
        })
      } else if (message.eventName === 'storage') {
        // 获取存储info
        this.setBindDeviceInfo({
          ...this.bindDeviceInfo,
          queryStorage: message.data
        })
      } else if (message.eventName === 'version') {
        // 获取软件、固件 版本
        this.setBindDeviceInfo({
          ...this.bindDeviceInfo,
          hardwareVersion: message.data.hardwareVersion
        })
      } else if (message.eventName === 'connect-success') {
        const attempt = this.connectionAttempt
        if (attempt) {
          if (attempt.rollingBack || message.data?.deviceId !== attempt.deviceId) return
          attempt.successData = message.data
          if (!attempt.commandsComplete) return
          this.connectionAttempt = null
        }
        if (!attempt) return
        this.completeConnection(message.data)
      } else if (message.eventName === 'connect-fail') {
        const attempt = this.connectionAttempt
        if (attempt?.rollingBack) return
        if (attempt?.reconnect) {
          this.failReconnect(attempt)
          return
        }
        this.setConnectingDeviceId('')
        if (attempt) {
          if (!attempt.bound) {
            attempt.failed = true
            return
          }
          await this.rollbackConnection(attempt)
          if (!attempt.newBinding) showToast('设备连接失败')
        } else {
          await this.startScanPolling()
          showToast('设备连接失败')
        }
      } else if (message.eventName === 'disconnect') {
        if (this.connectionAttempt) {
          if (this.connectionAttempt.reconnect) this.failReconnect(this.connectionAttempt)
          else if (this.connectionAttempt.bound) {
            await this.rollbackConnection(this.connectionAttempt)
          }
          return
        }
        if (!this.bindDeviceInfo.deviceId) return
        // 原生断连不代表解绑，保留后端绑定信息。
        this.reportBoundDeviceInfo({ connectStatus: 2 })
        this.setCurrentState(NVEASY_BLUETOOTH_STATE.BOUND_DISCONNECTED)
        await this.startScanPolling()
      } else if (message.eventName === 'file-list') {
        // 同步文件列表
        const files = message?.data?.files || []
        this.setFiles(files)
        if (files.length && !this.downloadInfo.totalCount) {
          this.setDownloadInfo({
            currentCount: 1,
            totalCount: files.length
          })
          this.downloadFiles({
            files
          })
        }
      } else if (message.eventName === 'download-finished') {
        this.setDownloadInfo({}, true)
        this.uploadLocalFiles()
      } else if (message.eventName === 'download-error') {
        this.setDownloadInfo({}, true)
      } else if (message.eventName === 'download-saved') {
        const currentCount = this.downloadInfo.currentCount + 1
        this.setDownloadInfo({
          currentCount
        })
      } else if (message.eventName === 'download-progress') {
        const { currentPacket, totalPackets } = message.data
        this.setDownloadInfo({
          currentPacket,
          totalPackets
        })
      } else if (message.eventName.startsWith('upload-')) {
        this.handleUploadEvent(message)
      } else if (message.eventName === 'record-state') {
        // record-state
        if (message.data.status === 0) {
          // 结束录音
          // 获取一次文件列表
          this.queryFiles()
        } else if (message.data.status === 1) {
          // 开始录音
          this.setDownloadInfo({}, true)
        }
      }
    },
    /**
     * 注册原生蓝牙桥事件监听。
     */
    registerBridge() {
      registerBluetoothBridge(this.handleBluetoothEvent)
    },
    /**
     * 清理原生蓝牙桥事件监听，避免页面销毁后重复回调。
     */
    clearBridge() {
      clearBluetoothBridge(this.handleBluetoothEvent)
    },
    /**
     * get---已绑定设备信息
     */
    async getBoundDeviceInfo() {
      const res = await getBluetoothBadgeBindingDetail()
      const deviceInfo = {
        deviceId: '',
        name: ''
      }
      if (res.result.bound) {
        deviceInfo.deviceId = res.result.deviceNo
        deviceInfo.name = res.result.name
      }
      this.bindingDetail = res.result.bound ? { ...res.result } : null
      // 保存配置
      this.setBindDeviceInfo({
        ...deviceInfo
      })
      return deviceInfo
    },
    /**
     * report---同步已绑定设备信息
     */
    async reportBoundDeviceInfo({ connectStatus = 0 }) {
      const reportDeviceId = this.bindDeviceInfo?.deviceId
      if (!reportDeviceId) return
      setTimeout(async () => {
        if (this.bindDeviceInfo?.deviceId !== reportDeviceId) return
        const {
          name,
          hardwareVersion,
          idMark,
          queryBattery = {},
          queryStorage = {}
        } = this.bindDeviceInfo
        const detail = this.bindingDetail?.deviceNo === reportDeviceId ? this.bindingDetail : null
        const textOrFallback = (value, fallback) => value || fallback || undefined
        const params = {
          deviceNo: reportDeviceId,
          name: textOrFallback(name, detail?.name),
          batteryPercent: queryBattery?.caseBattery ?? detail?.batteryPercent,
          storageTotalKb: queryStorage?.totalSpace ?? detail?.storageTotalKb,
          storageFreeKb: queryStorage?.freeSpace ?? detail?.storageFreeKb,
          model: textOrFallback(idMark, detail?.model),
          version: textOrFallback(hardwareVersion, detail?.version),
          connectStatus, // 连接状态：0未知、1已连接、2已断开
          statusCollectedAt: Math.floor(Date.now() / 1000) // APP采集状态的Unix秒级时间戳
        }
        await reportBluetoothBadgeStatus(params)
      }, 2000)
    },
    /**
     * 检查系统蓝牙是否可用。
     */
    async checkBluetoothEnabled() {
      const message = await callBluetooth('isBlueEnable')
      return message.data.data.enabled
    },
    /**
     * 播放控制(开始、停止、暂停、继续)
     */
    async startRecord() {
      await callBluetooth('startRecord')
      this.setPlayStatus(NVEASY_PLAY_STATE.START)
      return this.playStatus
    },
    async stopRecord() {
      await callBluetooth('stopRecord')
      this.setPlayStatus(NVEASY_PLAY_STATE.STOP)
      return this.playStatus
    },
    async pauseRecord() {
      await callBluetooth('pauseRecord')
      this.setPlayStatus(NVEASY_PLAY_STATE.PAUSE)
      return this.playStatus
    },
    async resumeRecord() {
      await callBluetooth('resumeRecord')
      this.setPlayStatus(NVEASY_PLAY_STATE.RESUME)
      return this.playStatus
    },
    /**
     * 检查原生侧是否已有设备连接。
     */
    async checkBluetoothConnected() {
      const message = await callBluetooth('isConnected')
      return message.data.data.connected
    },
    /**
     * 获取当前蓝牙设备详情，并补齐电量与容量信息。
     */
    async getDeviceInfo() {
      // 获取数据走event
      return await Promise.all([callBluetooth('queryBattery'), callBluetooth('queryStorage')])
    },
    /**
     * 查询文件列表。
     */
    async queryFiles() {
      return await callBluetooth('queryFiles')
    },
    /**
     * 初始化原生蓝牙能力与过滤配置。
     */
    initSDK() {
      this.registerBridge()
      return callBluetooth('init', NVEASY_BLUETOOTH_CONFIG)
    },
    /**
     * 开始扫描目标厂商设备，可选择保留当前业务状态。
     */
    async startScan(options = {}) {
      if (options.isInit) {
        this.setDevices([])
      }
      this.setScanning(true)
      await callBluetooth('scan', {
        manufacturerCode: NVEASY_BLUETOOTH_CONFIG.manufacturerCode
      })
    },
    /**
     * 开启扫描轮询，scan-finished 后自动发起下一轮扫描。
     * 注意：需要当前页面关闭及时释放
     */
    async startScanPolling() {
      this.setDevices([])
      if (this.scanPolling) return
      this.setScanPolling(true)
      await this.startScan()
    },
    /**
     * 结束扫描轮询，并停止当前扫描。
     */
    async stopScanPolling() {
      this.setScanPolling(false)
      await this.stopScan()
    },
    async initDevice(isPageInit = false) {
      this.setDevices([])
      this.setScanning(false)
      this.setConnectingDeviceId('')

      // API 获取绑定状态
      await this.getBoundDeviceInfo()

      // 判断蓝牙是否开启
      let bluetoothEnabled = false
      try {
        bluetoothEnabled = await this.checkBluetoothEnabled()
        if (!bluetoothEnabled) {
          this.setCurrentState(NVEASY_BLUETOOTH_STATE.PERMISSION_DISABLED)
          return
        }
      } catch (error) {
        this.setCurrentState(NVEASY_BLUETOOTH_STATE.PERMISSION_DISABLED)
        return
      }

      // 已经绑定BT设备
      if (this.bindDeviceInfo?.deviceId) {
        try {
          // 判断BT是否已经连接
          const connected = await this.checkBluetoothConnected()
          // BT已连接，不扫描
          if (connected) {
            this.setCurrentState(NVEASY_BLUETOOTH_STATE.BOUND_CONNECTED)
            // 绑定后会触发event 获取电量，此处手动获取存储空间
            this.refreshConnectedDeviceInfo()
            return
          }
          // BT未连接，扫描(连接)
          this.setCurrentState(NVEASY_BLUETOOTH_STATE.BOUND_DISCONNECTED)
          // 扫描设备
          if (isPageInit) {
            // 初始化扫描一次
            await this.startScan({ isInit: true })
          } else {
            // 页面中轮询扫描
            this.startScanPolling()
          }
        } catch (error) {
          console.log('🚀 ~ initDevice ~ error:', error)
          // 未绑定设备，扫描
          this.setCurrentState(NVEASY_BLUETOOTH_STATE.BOUND_DISCONNECTED)
        }
        return
      }

      // 未绑定设备，扫描
      this.setCurrentState(NVEASY_BLUETOOTH_STATE.UNBOUND)
      // 如果是 非页面 初始化
      // 页面中轮询扫描
      !isPageInit && (await this.startScanPolling())
    },
    async refreshConnectedDeviceInfo() {
      setTimeout(async () => {
        try {
          await this.getDeviceInfo()
          this.reportBoundDeviceInfo({ connectStatus: 1 })
        } catch (error) {
          console.warn('获取蓝牙设备信息失败', error)
        }
      }, 100)
    },
    /**
     * App 启动时初始化蓝牙，使用 initialized 防止重复初始化。
     */
    async initAppBluetooth(isPageInit = false) {
      if (!this.initialized) {
        // SDK 初始化
        this.setInitialized(true)
        await this.initSDK()
      }

      try {
        // BT 初始化
        await this.initDevice(isPageInit)
      } catch (error) {
        this.setInitialized(false)
      }
    },
    /**
     * 原生连接确认后更新状态。
     */
    completeConnection(data) {
      const queryBattery =
        this.bindDeviceInfo?.deviceId === data.deviceId
          ? this.bindDeviceInfo.queryBattery
          : undefined
      showToast('设备连接成功', 'success')
      this.setConnectingDeviceId('')
      this.setCurrentState(NVEASY_BLUETOOTH_STATE.BOUND_CONNECTED)
      this.setScanPolling(false)
      this.setScanning(false)
      this.setBindDeviceInfo({
        idMark: data.idMark,
        name: data.name,
        deviceId: data.deviceId,
        queryBattery
      })
      this.refreshConnectedDeviceInfo()
    },
    /**
     * 回滚本次新建的绑定；原生清理失败不阻止后端解绑。
     */
    async rollbackConnection(attempt) {
      if (!attempt || this.connectionAttempt !== attempt || attempt.rollingBack) return
      attempt.rollingBack = true
      this.setConnectingDeviceId('')
      if (attempt.newBinding) {
        this.setBindDeviceInfo({ deviceId: attempt.deviceId, name: attempt.device.name })
        this.setCurrentState(NVEASY_BLUETOOTH_STATE.BOUND_DISCONNECTED)
      }

      const unbindResult = attempt.newBinding
        ? unbindBluetoothBadge({ deviceNo: attempt.deviceId })
            .then(() => true)
            .catch((error) => {
              console.warn('回滚设备绑定失败', error)
              return false
            })
        : Promise.resolve(true)
      let nativeCleaned = true
      if (attempt.nativeStarted) {
        try {
          await callBluetooth('unboundDevice')
        } catch (error) {
          nativeCleaned = false
          console.warn('清理原生设备绑定失败', error)
        }
        try {
          await callBluetooth('disconnect')
        } catch (error) {
          nativeCleaned = false
          console.warn('清理原生连接失败', error)
        }
      }

      const unbound = await unbindResult
      if (unbound && attempt.newBinding) showToast('设备连接失败')
      if (!unbound) showToast('连接失败，设备解绑失败，请手动解绑')
      if (!nativeCleaned) showToast('原生连接清理失败，请检查设备连接状态')
      if (this.connectionAttempt === attempt) {
        this.connectionAttempt = null
        if (unbound && attempt.newBinding) {
          this.setBindDeviceInfo({})
          this.setCurrentState(NVEASY_BLUETOOTH_STATE.UNBOUND)
        }
        if (!unbound) {
          this.setBindDeviceInfo({ deviceId: attempt.deviceId, name: attempt.device.name })
          this.setCurrentState(NVEASY_BLUETOOTH_STATE.BOUND_DISCONNECTED)
        }
        if (!this.scanPolling) this.startScanPolling().catch(() => {})
      }
    },
    /**
     * 已绑定设备重连失败时保持绑定关系并继续扫描。
     */
    failReconnect(attempt) {
      if (this.connectionAttempt !== attempt || attempt.failed) return
      attempt.failed = true
      this.connectionAttempt = null
      this.setConnectingDeviceId('')
      this.setCurrentState(NVEASY_BLUETOOTH_STATE.BOUND_DISCONNECTED)
      showToast('设备连接失败')
      if (!this.scanPolling) this.startScanPolling().catch(() => {})
    },
    /**
     * 扫描到已绑定设备时，仅发起原生连接和设备绑定指令。
     */
    async reconnectBoundDevice(device) {
      const deviceId = device?.deviceId
      if (
        !deviceId ||
        deviceId !== this.bindDeviceInfo?.deviceId ||
        this.isConnected ||
        this.connectingDeviceId ||
        this.connectionAttempt
      )
        return false
      const attempt = { deviceId, reconnect: true, commandsComplete: false }
      this.connectionAttempt = attempt
      this.setConnectingDeviceId(deviceId)
      try {
        await callBluetooth('connect', { deviceId, mac: device.mac })
        if (attempt.failed) return false
        await callBluetooth('boundDevice')
        if (attempt.failed) return false
        try {
          await callBluetooth('stopScan')
          this.setScanning(false)
          this.setScanPolling(false)
        } catch (error) {
          console.warn('停止蓝牙扫描失败', error)
        }
        if (attempt.failed) return false
        attempt.commandsComplete = true
        if (attempt.successData) {
          this.connectionAttempt = null
          this.completeConnection(attempt.successData)
        }
        return true
      } catch (error) {
        this.failReconnect(attempt)
        return false
      }
    },
    /**
     * 未绑定状态下手动连接并绑定指定设备，成功后保存绑定信息。
     */
    async connectDevice(device) {
      const deviceId = device.deviceId
      if (!deviceId || this.isBind || this.connectingDeviceId || this.connectionAttempt) {
        return false
      }
      this.setConnectingDeviceId(deviceId)
      let attempt
      try {
        const {
          result: { status }
        } = await checkBluetoothBadgeConnect({ deviceNo: deviceId })
        if ([0, 1].includes(Number(status))) {
          const newBinding = Number(status) === 0
          attempt = { deviceId, device, newBinding, nativeStarted: false, rollingBack: false }
          this.connectionAttempt = attempt
          await bindBluetoothBadge({ deviceNo: deviceId })
          attempt.bound = true
          if (attempt.failed) {
            await this.rollbackConnection(attempt)
            return false
          }
          attempt.nativeStarted = true
          await callBluetooth('connect', {
            deviceId,
            mac: device.mac
          })
          if (attempt.rollingBack) return false
          await callBluetooth('boundDevice')
          if (attempt.rollingBack) return false
          // 停止扫描失败不代表连接失败，不回滚已经建立的连接。
          try {
            await callBluetooth('stopScan')
            this.setScanning(false)
            this.setScanPolling(false)
          } catch (error) {
            console.warn('停止蓝牙扫描失败', error)
          }
          attempt.commandsComplete = true
          if (attempt.rollingBack) return false
          if (attempt.successData) {
            this.connectionAttempt = null
            this.completeConnection(attempt.successData)
          }
          return true
        } else {
          let errorText = ''
          switch (Number(status)) {
            case 2:
              errorText = '设备已归属其他企业'
              break
            case 3:
              errorText = '设备已绑定当前企业的其他员工'
              break
            case 4:
              errorText = '当前企业蓝牙卡片增值套餐不存在或已过期'
              break
            case 5:
              errorText = '当前企业蓝牙卡片使用人数已达到套餐上限'
              break
            default:
              errorText = '设备连接错误：' + status
              break
          }
          showToast(errorText)
          return false
        }
      } catch (error) {
        const alreadyRollingBack = attempt?.rollingBack
        if (attempt?.nativeStarted && !alreadyRollingBack) {
          await this.rollbackConnection(attempt)
        } else if (attempt && this.connectionAttempt === attempt && !alreadyRollingBack) {
          this.connectionAttempt = null
        }
        if (!alreadyRollingBack && (!attempt?.newBinding || !attempt.bound)) {
          showToast('设备连接失败')
        }
        return false
      } finally {
        if (
          !this.connectionAttempt ||
          (this.connectionAttempt === attempt && !attempt.nativeStarted)
        ) {
          this.setConnectingDeviceId('')
        }
      }
    },
    /**
     * 解绑当前设备并重置为未绑定状态。
     */
    async disconnectDevice() {
      try {
        const wasConnected = this.isConnected
        if (wasConnected) {
          await callBluetooth('unboundDevice')
          await callBluetooth('disconnect')
        }
        await unbindBluetoothBadge({ deviceNo: this.bindDeviceInfo.deviceId })
        this.setBindDeviceInfo({})
        this.setCurrentState(NVEASY_BLUETOOTH_STATE.UNBOUND)
        return true
      } catch (error) {
        showToast('解绑失败')
        return false
      }
    },
    /**
     * 停止原生扫描并同步页面扫描状态。
     */
    async stopScan() {
      await callBluetooth('stopScan')
      this.setScanning(false)
      this.setScanPolling(false)
    },
    /**
     * 下载文件
     */
    async downloadFiles({ files = [], deleteAfterDownload = false, useWifi = false }) {
      const snList = files.map((file) => {
        return { sn: Number(file.sn), offset: file.offset || 0 }
      })

      return await callBluetooth('downloadFiles', {
        files: snList,
        deleteAfterDownload,
        useWifi
      })
    },
    /**
     * 同步本地接收文件到oss
     */
    async uploadLocalFiles() {
      if (window.uni) {
        const ossFileList = this.ossFiles.map((item) => ({
          name: item.fileName.replace('.wav', '.opus')
        }))
        // 发送sts签名给uniapp
        getSignature().then((res) => {
          uni.postMessage({
            data: {
              type: 'uploadNVEasyBTLocalFile',
              ossConfig: {
                ...res.result,
                corpid: utils.xbbLocalStorageGet('corpid'),
                userId: utils.xbbLocalStorageGet('userId')
              },
              // 这个列表采用oss的列表
              ossFileList
            }
          })
        })
      }
    },
    /**
     * 获取oss上传的文件列表（最近200条）
     */
    async getOssFiles() {
      const { result: { list: ossFiles = [] } = {} } = await getBluetoothBadgeRecordList({
        deviceNo: this.bindDeviceInfo.deviceId,
        page: 1,
        pageSize: 100
      })
      console.log('🚀 ~ getOssFiles ~ ossFiles:', ossFiles)
      this.setOssFiles(ossFiles)
      return ossFiles
    },
    openBluetoothSetting(type) {
      callBluetooth('openBluetoothSetting', type)
    }
  }
})
